import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  MapPin, Building2, CalendarDays, QrCode, UserPlus, WifiOff, CheckCircle2,
  Clock, RefreshCw, ChevronRight, ScanLine,
} from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import {
  pendingCheckins, pendingLeads, syncQueue, syncLeads, queueCheckin,
} from "../lib/offlineQueue";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { StatusBadge } from "../components/StatusBadge";
import { MissionProgress } from "../components/MissionProgress";
import { MissionDetailSheet } from "../components/MissionDetailSheet";
import { ManualLeadSheet } from "../components/ManualLeadSheet";
import { localeOf } from "../i18n";

export default function FieldMode() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [ctx, setCtx] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [queuedCheckins, setQueuedCheckins] = useState([]);
  const [queuedLeads, setQueuedLeads] = useState([]);
  const [mission, setMission] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [picked, setPicked] = useState("");

  const readQueues = useCallback(async () => {
    setQueuedCheckins(await pendingCheckins());
    setQueuedLeads(await pendingLeads());
  }, []);

  const load = useCallback(async (evId) => {
    try {
      const { data } = await api.get("/me/field-context",
        { params: evId ? { event_id: evId } : {} });
      setCtx(data);
    } catch (e) {
      if (e.response) toast.error(apiError(e));
    }
    readQueues();
  }, [readQueues]);

  useEffect(() => {
    load();
    const onOnline = async () => {
      setOffline(false);
      const n = await syncQueue();
      const leadRes = await syncLeads();
      if (n || leadRes.synced) toast.success(t("field.synced"));
      if (leadRes.blocked) toast.error(t("field.syncBlocked", { n: leadRes.blocked }));
      load();
    };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const part = ctx?.participation;
  const evId = ctx?.event?.event_id;
  // Event-scoped: a queued check-in for another event must not show here.
  const pendingForThisEvent = !part?.checked_in_at && queuedCheckins.some(
    (q) => q.event_id === evId || (!q.event_id && ctx?.selectable_events?.length === 1));
  const checkinState = part?.checked_in_at ? "confirmed"
    : pendingForThisEvent ? "pending_sync" : "none";

  const doCheckin = async () => {
    const value = code.trim().toUpperCase();
    if (!value) return;
    setBusy(true);
    try {
      if (!navigator.onLine) {
        await queueCheckin({ code: value, event_id: evId, client_timestamp: new Date().toISOString() });
        toast.info(t("checkin.offlineQueued"));
      } else {
        await api.post("/checkin", { code: value });
        toast.success(t("checkin.success"));
      }
      setCode("");
      await load(picked || undefined);
    } catch (e) {
      if (!e.response) {
        await queueCheckin({ code: value, event_id: evId, client_timestamp: new Date().toISOString() });
        toast.info(t("checkin.offlineQueued"));
        await load();
      } else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const openQr = async () => {
    setQrOpen(true);
    if (!qr) {
      try {
        const { data } = await api.get("/me/qr");
        setQr(data);
      } catch (e) { toast.error(apiError(e)); }
    }
  };

  const runSync = async () => {
    const n = await syncQueue();
    const leadRes = await syncLeads();
    if (n || leadRes.synced) toast.success(t("field.synced"));
    if (leadRes.blocked) toast.error(t("field.syncBlocked", { n: leadRes.blocked }));
    await load(picked || undefined);
  };

  if (!ctx) return <p className="py-10 text-center text-muted-foreground">{t("common.loading")}</p>;

  const ev = ctx.event;
  const pendingTotal = queuedCheckins.length + queuedLeads.length;

  return (
    <div className="space-y-5 pb-4" data-testid="field-mode-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">{t("field.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("field.subtitle")}</p>
      </div>

      {(offline || pendingTotal > 0) && (
        <div className="rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4"
          data-testid="field-offline-banner">
          <p className="flex items-center gap-2 text-sm font-bold">
            <WifiOff className="h-4 w-4" />
            {offline ? t("field.offlineMode") : t("field.pendingActions")}
          </p>
          <ul className="mt-2 space-y-1 text-xs font-semibold text-muted-foreground">
            <li data-testid="field-pending-checkins">
              {t("field.pendingCheckins", { n: queuedCheckins.length })}
            </li>
            <li data-testid="field-pending-leads">
              {t("field.pendingLeads", { n: queuedLeads.length })}
            </li>
          </ul>
          {queuedLeads.some((l) => l.needs_event || l.last_error) && (
            <p className="mt-2 text-xs font-bold text-destructive" data-testid="field-stuck-leads">
              {t("field.stuckLeads")}
            </p>
          )}
          {!offline && pendingTotal > 0 && (
            <Button data-testid="field-sync-btn" variant="outline" onClick={runSync}
              className="mt-3 h-11 w-full rounded-full font-bold">
              <RefreshCw className="mr-2 h-4 w-4" />{t("qr.offlineSyncNow")}
            </Button>
          )}
        </div>
      )}

      {!ev && (
        <div className="vy-card p-5" data-testid="field-no-event">
          <p className="text-sm font-semibold">{t("field.noEvent")}</p>
          {ctx.selectable_events?.length > 0 ? (
            <>
              <p className="mt-1 text-xs text-muted-foreground">{t("field.chooseEvent")}</p>
              <select data-testid="field-event-select" value={picked}
                onChange={async (e) => { setPicked(e.target.value); await load(e.target.value); }}
                className="mt-2 h-12 w-full rounded-md border border-input bg-background px-3 text-sm font-semibold">
                <option value="">{t("capture.noEvent")}</option>
                {ctx.selectable_events.map((e) => (
                  <option key={e.id} value={e.id}>{e.title}</option>
                ))}
              </select>
            </>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">{t("field.noEventHint")}</p>
          )}
        </div>
      )}

      {ev && (
        <>
          <section className="vy-card p-5" data-testid="field-event-card">
            <h2 className="font-display text-xl font-bold" data-testid="field-event-title">
              {ev.event_title}
            </h2>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />{fmtDate(ev.starts_at, locale)}
              </li>
              {ev.location && (
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4" />{ev.location}</li>
              )}
              {ev.partner_name && (
                <li className="flex items-center gap-2"><Building2 className="h-4 w-4" />{ev.partner_name}</li>
              )}
              {ev.campaign_name && (
                <li className="flex items-center gap-2"><ScanLine className="h-4 w-4" />{ev.campaign_name}</li>
              )}
            </ul>
            <p className={`mt-3 flex items-center gap-1.5 text-xs font-bold ${
              checkinState === "confirmed" ? "text-[hsl(var(--success))]"
                : checkinState === "pending_sync" ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}
              data-testid="field-checkin-state" data-state={checkinState}>
              {checkinState === "confirmed" ? <CheckCircle2 className="h-3.5 w-3.5" />
                : checkinState === "pending_sync" ? <WifiOff className="h-3.5 w-3.5" />
                  : <Clock className="h-3.5 w-3.5" />}
              {t(`checkin.state${checkinState === "confirmed" ? "Confirmed"
                : checkinState === "pending_sync" ? "PendingSync" : "None"}`)}
            </p>

            {checkinState !== "confirmed" && (
              <div className="mt-4 space-y-2">
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {t("checkin.enterCode")}
                </label>
                <Input data-testid="field-checkin-code" value={code} inputMode="text"
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="h-14 text-center text-lg font-bold tracking-widest" maxLength={12} />
                <Button data-testid="field-checkin-btn" onClick={doCheckin} disabled={busy || !code.trim()}
                  className="h-14 w-full rounded-full text-base font-bold">
                  {t("field.doCheckin")}
                </Button>
              </div>
            )}
          </section>

          <section className="vy-card p-5" data-testid="field-leads-card">
            <h2 className="font-display text-lg font-bold">{t("field.captureLead")}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Button data-testid="field-show-qr-btn" onClick={openQr}
                className="h-14 rounded-full text-base font-bold">
                <QrCode className="mr-2 h-5 w-5" />{t("field.showQr")}
              </Button>
              <Button data-testid="field-manual-lead-btn" variant="outline"
                onClick={() => setManualOpen(true)}
                className="h-14 rounded-full text-base font-bold">
                <UserPlus className="mr-2 h-5 w-5" />{t("field.addManual")}
              </Button>
            </div>
            {qrOpen && (
              <div className="mt-4 text-center" data-testid="field-qr-panel">
                {qr ? (
                  <>
                    <img src={qr.qr_png} alt="QR" className="mx-auto w-48 rounded-2xl border border-border" />
                    <p className="mt-2 break-all text-xs text-muted-foreground">{qr.url}</p>
                  </>
                ) : <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
              </div>
            )}
            {ctx.leads && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[["total", ctx.leads.total], ["qr", ctx.leads.qr_self_service],
                  ["manual", ctx.leads.manual_ambassador]].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-muted p-3 text-center" data-testid={`field-lead-${k}`}>
                    <p className="font-display text-xl font-extrabold">{v}</p>
                    <p className="text-[11px] font-bold uppercase text-muted-foreground">
                      {t(`fieldLeads.${k}`)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {ctx.progress && <MissionProgress progress={ctx.progress} checkinState={checkinState}
            testId="field-progress" />}

          <section className="space-y-3" data-testid="field-missions-section">
            <h2 className="font-display text-lg font-bold">{t("field.myMissions")}</h2>
            {ctx.missions.map((m) => (
              <button key={m.id} type="button" onClick={() => setMission(m)}
                data-testid={`field-mission-${m.id}`}
                className="vy-card flex w-full items-center gap-3 p-4 text-left transition-transform active:scale-[0.99]">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-bold">{m.title}</p>
                  <p className="text-xs font-semibold text-muted-foreground">
                    {t(`missions.${m.proof_type}`)} · {m.points} {t("common.points")}
                  </p>
                </div>
                <StatusBadge status={m.my_submission?.status || "new"}
                  testId={`field-mission-status-${m.id}`} />
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </section>
        </>
      )}

      <MissionDetailSheet mission={mission}
        pendingCheckinSync={checkinState === "pending_sync"}
        canSubmit={!!(part?.checked_in_at && ev?.status !== "cancelled"
          && mission?.my_submission?.status !== "approved")}
        onClose={() => setMission(null)}
        onDone={async () => { setMission(null); await load(); }} />

      <ManualLeadSheet open={manualOpen} onClose={() => setManualOpen(false)}
        onDone={async () => { setManualOpen(false); await load(); }}
        activeEvent={ctx.active_event} selectableEvents={ctx.selectable_events || []} />
    </div>
  );
}
